import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { AxiosError } from "axios";

import { getDossier } from "@/api/objekte";
import { DossierTabs } from "@/components/objekte/DossierTabs";
import { PhaseBadge } from "@/components/PhaseBadge";
import { PhaseEditor } from "@/components/objekte/PhaseEditor";
import { PhotoGallery } from "@/components/objekte/PhotoGallery";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { formatEur, formatRelative } from "@/lib/format";

interface Props {
  slug: string;
}

export function ObjektDetailPage({ slug }: Props) {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["dossier", slug],
    queryFn: () => getDossier(slug),
    refetchOnMount: true,
  });

  return (
    <main className="mx-auto max-w-5xl px-6 py-8 space-y-6">
      <div>
        <Link
          to="/objekte"
          className="text-sm text-muted-foreground hover:underline"
        >
          ← Alle Objekte
        </Link>
      </div>

      {isLoading && <DetailLoading />}

      {isError && (
        <ErrorPanel
          error={error}
          onRetry={() => void refetch()}
          slug={slug}
        />
      )}

      {data && (
        <>
          <header className="space-y-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h1 className="font-mono text-xl font-semibold">{slug}</h1>
                {data.state.address_short && (
                  <p className="mt-1 text-sm text-muted-foreground">
                    {data.state.address_short}
                  </p>
                )}
              </div>
              <PhaseBadge phase={data.state.phase} />
            </div>
            <dl className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground">
              <Stat
                label="Kaufpreis"
                value={formatEur(data.state.kaufpreis_eur)}
              />
              <Stat
                label="Letzte Aktualisierung"
                value={formatRelative(data.state.last_updated)}
              />
              {data.state.source_url && (
                <Stat
                  label="Quelle"
                  value={
                    <a
                      href={data.state.source_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary underline underline-offset-2"
                    >
                      Inserat öffnen
                    </a>
                  }
                />
              )}
            </dl>
          </header>

          <PhaseEditor state={data.state} />

          <DossierTabs slug={slug} sections={data.sections} />

          <PhotoGallery slug={slug} photos={data.photos} />
        </>
      )}
    </main>
  );
}

function Stat({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide">{label}</dt>
      <dd className="text-foreground tabular-nums">{value}</dd>
    </div>
  );
}

function DetailLoading() {
  return (
    <div className="space-y-4" data-testid="dossier-loading">
      <Skeleton className="h-8 w-1/3" />
      <Skeleton className="h-4 w-1/4" />
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-64 w-full" />
    </div>
  );
}

function ErrorPanel({
  error,
  onRetry,
  slug,
}: {
  error: unknown;
  onRetry: () => void;
  slug: string;
}) {
  const is404 = error instanceof AxiosError && error.response?.status === 404;
  return (
    <div
      role="alert"
      className="rounded-lg border border-destructive/40 bg-destructive/5 p-6"
      data-testid="dossier-error"
    >
      <p className="text-sm font-medium text-destructive">
        {is404 ? "Objekt nicht gefunden" : "Dossier konnte nicht geladen werden"}
      </p>
      <p className="mt-1 text-xs text-muted-foreground">
        {is404
          ? `Slug ${slug} ist nicht in der SQLite-Index. Existiert das Objekt im Repo (objekte/${slug}/)?`
          : error instanceof Error
            ? error.message
            : "Unbekannter Fehler."}
      </p>
      {!is404 && (
        <Button variant="outline" size="sm" className="mt-3" onClick={onRetry}>
          Erneut versuchen
        </Button>
      )}
    </div>
  );
}
